import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { strengthColor, strengthIndicator } from 'utils/password-strength';
import { updateUserPassword } from 'firebaseService/firebaseAuth';
import { showAlertWithAutoClose } from 'ui-component/Alerts/SweetAlerts';

const ChangePasswordModal = ({ open, onClose }) => {
  const theme = useTheme();
  const [strength, setStrength] = useState(0);
  const [level, setLevel] = useState();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentPasswordError, setCurrentPasswordError] = useState(null);
  const [newPasswordError, setNewPasswordError] = useState(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState(null);
  const [passwordUpdateError, setPasswordUpdateError] = useState(null);

  const handleCurrentPasswordChange = (event) => {
    setCurrentPassword(event.target.value);
  };

  const handleNewPasswordChange = (event) => {
    setNewPassword(event.target.value);
  };

  const handleConfirmPasswordChange = (event) => {
    setConfirmPassword(event.target.value);
  };

  const handleClickShowPassword = (passwordType) => {
    switch (passwordType) {
      case 'current':
        setShowCurrentPassword(!showCurrentPassword);
        break;
      case 'new':
        setShowNewPassword(!showNewPassword);
        break;
      case 'confirm':
        setShowConfirmPassword(!showConfirmPassword);
        break;
      default:
        break;
    }
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const changePassword = (value) => {
    const temp = strengthIndicator(value);
    setStrength(temp);
    setLevel(strengthColor(temp));
  };

  const clearMsg = () => {
    setCurrentPasswordError(null);
    setNewPasswordError(null);
    setConfirmPasswordError(null);
    setPasswordUpdateError(null);
  };

  const handleUpdatePassword = async () => {
    try {
      clearMsg();
      if (newPassword !== confirmPassword) {
        setConfirmPasswordError('La nueva contraseña y la confirmación no coinciden.');
        return;
      }

      if (strength < 4) {
        setNewPasswordError(
          `La contraseña no cumple con los requisitos mínimos de seguridad. Asegúrese de que su contraseña tenga al menos una calificación de "Aceptable" según nuestra escala de fortaleza.`
        );
        return;
      }

      const result = await updateUserPassword(currentPassword, newPassword);

      if (result.success) {
        await showAlertWithAutoClose('Contraseña actualizada', 'Su contraseña ha sido actualizada con éxito', 'success', 3000);
        onClose();
      } else {
        if (result.error.message.includes('auth/invalid-login-credentials')) {
          setCurrentPasswordError('La contraseña actual es incorrecta.');
        } else if (result.error.message.includes('auth/too-many-requests')) {
          setPasswordUpdateError('Demasiados intentos fallidos de cambio de contraseña. Por favor, intente nuevamente más tarde.');
        } else {
          throw result.error;
        }
      }
    } catch (error) {
      console.error('Error al intentar actualizar la contraseña:', error);
      setPasswordUpdateError('Error inesperado al intentar actualizar la contraseña. Por favor, inténtalo de nuevo.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={'sm'} fullWidth>
      <DialogTitle>Actualizar Contraseña</DialogTitle>
      <DialogContent>
        <FormControl error={Boolean(currentPasswordError)} fullWidth sx={{ ...theme.typography.customInput }}>
          <InputLabel htmlFor="outlined-adornment-password-login">Contraseña actual</InputLabel>
          <OutlinedInput
            id="outlined-adornment-password-login"
            type={showCurrentPassword ? 'text' : 'password'}
            value={currentPassword}
            name="password"
            onChange={handleCurrentPasswordChange}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => handleClickShowPassword('current')}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  size="large"
                >
                  {showCurrentPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            }
            label="Contraseña actual"
            inputProps={{}}
          />
        </FormControl>
        {currentPasswordError && (
          <Typography variant="body2" color="error" sx={{ marginTop: 1 }}>
            {currentPasswordError}
          </Typography>
        )}
        <FormControl fullWidth error={Boolean(newPasswordError)} sx={{ ...theme.typography.customInput }}>
          <InputLabel htmlFor="outlined-adornment-password-login">Nueva contraseña</InputLabel>
          <OutlinedInput
            id="outlined-adornment-password-login"
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            name="password"
            onChange={(e) => {
              handleNewPasswordChange(e);
              changePassword(e.target.value);
            }}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => handleClickShowPassword('new')}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  size="large"
                >
                  {showNewPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            }
            label="Nueva contraseña"
            inputProps={{}}
          />
        </FormControl>
        {newPasswordError && (
          <Typography variant="body2" color="error" sx={{ marginTop: 1 }}>
            {newPasswordError}
          </Typography>
        )}
        <FormControl fullWidth error={Boolean(confirmPasswordError)} sx={{ ...theme.typography.customInput }}>
          <InputLabel htmlFor="outlined-adornment-password-login">Confirmar nueva contraseña</InputLabel>
          <OutlinedInput
            id="outlined-adornment-password-login"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            name="password"
            onChange={handleConfirmPasswordChange}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => handleClickShowPassword('confirm')}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  size="large"
                >
                  {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            }
            label="Confirmar nueva contraseña"
            inputProps={{}}
          />
        </FormControl>
        {confirmPasswordError && (
          <Typography variant="body2" color="error" sx={{ marginTop: 1 }}>
            {confirmPasswordError}
          </Typography>
        )}
        {newPassword && strength !== 0 && (
          <FormControl fullWidth>
            <Box sx={{ mb: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <Box style={{ backgroundColor: level?.color }} sx={{ width: 85, height: 8, borderRadius: '7px' }} />
                </Grid>
                <Grid item>
                  <Typography variant="subtitle1" fontSize="0.75rem">
                    {level?.label}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </FormControl>
        )}
        {passwordUpdateError && (
          <Typography variant="body2" color="error" sx={{ marginTop: 1 }}>
            {passwordUpdateError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={handleUpdatePassword} variant="contained">
          Actualizar Contraseña
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordModal;
